import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Button, Chip, Tabs } from '@kontentino/ui';
import useInfiniteScroll from 'utils/hooks/useInfiniteScroll';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import { UserPermission } from 'constants/userPermission';
import { useUser } from 'modules/user/userSelector';
import {
  InAppNotificationFilter,
  InAppNotificationTab,
} from 'types/InAppNotification';
import useInAppNotificationsQuery from 'app/modules/inAppNotifications/hooks/useInAppNotificationsQuery';
import useInAppNotificationsSummaryQuery from 'app/modules/inAppNotifications/hooks/useInAppNotificationsSummaryQuery';
import { useMarkAllNotificationsAsRead } from 'app/modules/inAppNotifications/hooks/useInAppNotificationActions';
import NotificationRow from 'app/modules/inAppNotifications/components/NotificationRow';

const TABS: {
  value: InAppNotificationTab;
  labelKey: string;
  emptyKey: string;
  filters: InAppNotificationFilter[];
}[] = [
  {
    value: 'collaboration',
    labelKey: 'notifications.tabs.collaboration',
    emptyKey: 'notifications.emptyCollaboration',
    filters: ['unread', 'comments', 'approvals', 'tasks'],
  },
  {
    value: 'alerts',
    labelKey: 'notifications.tabs.alerts',
    emptyKey: 'notifications.emptyAlerts',
    filters: ['unread', 'connections', 'post_failures'],
  },
];

const FILTER_LABEL_KEY: Record<InAppNotificationFilter, string> = {
  unread: 'notifications.filters.unread',
  comments: 'notifications.filters.comments',
  approvals: 'notifications.filters.approvals',
  tasks: 'notifications.filters.tasks',
  connections: 'notifications.filters.connections',
  post_failures: 'notifications.filters.postFailures',
};

export function isInAppNotificationFilter(
  value: string | null,
): value is InAppNotificationFilter {
  return Object.keys(FILTER_LABEL_KEY).some((filter) => filter === value);
}

type FeedView = {
  tab: InAppNotificationTab;
  filter?: InAppNotificationFilter;
};

type Props = {
  initialTab?: InAppNotificationTab;
  initialFilter?: InAppNotificationFilter;
  footer?: (view: FeedView) => ReactNode;
  onViewChange?: (view: FeedView) => void;
};

function NotificationsFeed({
  initialTab,
  initialFilter,
  footer,
  onViewChange,
}: Props) {
  const { t } = useTranslation();
  const { isClient } = useUser();
  const { hasSome } = useUserPermissions();
  const { data: summary } = useInAppNotificationsSummaryQuery();
  const [selectedTab, setSelectedTab] = useState(initialTab);
  const [filter, setFilter] = useState(initialFilter);
  const { mutate: markAllAsRead } = useMarkAllNotificationsAsRead();

  const canApprove = hasSome([
    UserPermission.POST_INTERNAL_APPROVE,
    UserPermission.POST_CLIENT_APPROVE,
  ]);
  const canManageProfiles = hasSome([
    UserPermission.PAGE_EDIT,
    UserPermission.ACCESS_SOCIAL_LOGIN,
  ]);
  const canSchedule = hasSome([UserPermission.POST_SCHEDULE]);
  const canHandleTasks = hasSome([
    UserPermission.TASK_ASSIGNABLE,
    UserPermission.USER_ASSIGN_TASK,
  ]);
  const visibleTabs = TABS.filter(
    ({ value }) => value !== 'alerts' || !isClient,
  )
    .map((tabOption) => ({
      ...tabOption,
      filters: tabOption.filters.filter(
        (filterValue) =>
          (filterValue !== 'approvals' || canApprove) &&
          (filterValue !== 'tasks' || canHandleTasks) &&
          (filterValue !== 'connections' || canManageProfiles) &&
          (filterValue !== 'post_failures' || canSchedule),
      ),
    }))
    .filter(
      ({ value, filters }) =>
        value !== 'alerts' || filters.some((filter) => filter !== 'unread'),
    );
  const hasAlertsTab = visibleTabs.some(({ value }) => value === 'alerts');

  const isSelectedTabVisible = visibleTabs.some(
    ({ value }) => value === selectedTab,
  );
  const defaultTab: InAppNotificationTab =
    hasAlertsTab && summary?.alerts.hasCritical ? 'alerts' : 'collaboration';
  const tab = selectedTab && isSelectedTabVisible ? selectedTab : defaultTab;

  if (!isSelectedTabVisible && summary) {
    setSelectedTab(tab);
  }

  const { emptyKey, filters } =
    visibleTabs.find(({ value }) => value === tab) ?? visibleTabs[0];
  const activeFilter = filter && filters.includes(filter) ? filter : undefined;
  const unreadCount = summary
    ? summary.collaboration.unread + (hasAlertsTab ? summary.alerts.unread : 0)
    : 0;

  const {
    notifications,
    isLoading,
    isError,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInAppNotificationsQuery({ tab, filter: activeFilter });

  const [sentryRef, { rootRef }] = useInfiniteScroll({
    loading: isLoading || isFetchingNextPage,
    hasNextPage: !!hasNextPage,
    onLoadMore: fetchNextPage,
    disabled: isError,
    rootMargin: '0px 0px 200px 0px',
  });

  const handleTabChange = (value: string) => {
    const nextTab = visibleTabs.find((option) => option.value === value);

    if (nextTab) {
      setSelectedTab(nextTab.value);
      setFilter(undefined);
      onViewChange?.({ tab: nextTab.value });
    }
  };

  const handleFilterChange = (nextFilter?: InAppNotificationFilter) => {
    setFilter(nextFilter);
    onViewChange?.({ tab, filter: nextFilter });
  };

  return (
    <div className="tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-xl tw-bg-white tw-shadow-md tw-outline-none">
      <div className="tw-flex tw-min-h-[44px] tw-items-center tw-justify-between tw-gap-2 tw-px-4 tw-pb-2 tw-pt-3">
        <div className="tw-text-md tw-font-semibold tw-text-grayscale-180">
          {t('notifications.title')}
        </div>
        {unreadCount > 0 && (
          <Button
            variant="plain"
            size="small"
            onClick={() => markAllAsRead()}
            data-cy="navbar_notifications_mark-all-as-read"
          >
            {t('notifications.markAllAsRead')}
          </Button>
        )}
      </div>
      <div className="tw-border-b tw-border-grayscale-10 tw-px-4">
        <Tabs.Root value={tab} onValueChange={handleTabChange}>
          <Tabs.List>
            {visibleTabs.map(({ value, labelKey }) => (
              <Tabs.Tab
                key={value}
                value={value}
                data-cy={`navbar_notifications_tab-${value}`}
              >
                <span className="tw-flex tw-items-center tw-gap-2">
                  {t(labelKey)}
                  {summary && (
                    <Chip variant={value === tab ? 'primary' : 'grey'}>
                      {summary[value].unread}
                    </Chip>
                  )}
                </span>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs.Root>
      </div>
      <div className="tw-flex tw-flex-wrap tw-gap-2 tw-px-4 tw-pt-3">
        <Button
          variant={activeFilter ? 'secondary' : 'primary'}
          size="small"
          onClick={() => handleFilterChange(undefined)}
          data-cy="navbar_notifications_filter-all"
        >
          {t('all')}
        </Button>
        {filters.map((filterValue) => (
          <Button
            key={filterValue}
            variant={activeFilter === filterValue ? 'primary' : 'secondary'}
            size="small"
            onClick={() => handleFilterChange(filterValue)}
            data-cy={`navbar_notifications_filter-${filterValue}`}
          >
            {t(FILTER_LABEL_KEY[filterValue])}
          </Button>
        ))}
      </div>
      <div
        ref={rootRef}
        className="tw-flex tw-flex-1 tw-flex-col tw-gap-3 tw-overflow-y-auto tw-p-4"
        data-cy="navbar_notifications_list"
      >
        {isLoading && (
          <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
            <ActivityIndicator className="tw-text-primary-100" />
          </div>
        )}
        {!isLoading && isError && notifications.length === 0 && (
          <div className="tw-flex tw-h-full tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-text-center tw-text-sm tw-text-grayscale-90">
            {t('somethingWentWrong')}
            <Button
              variant="secondary"
              size="small"
              onClick={() => refetch()}
              data-cy="navbar_notifications_retry"
            >
              {t('retry')}
            </Button>
          </div>
        )}
        {!isLoading && !isError && notifications.length === 0 && (
          <div
            className="tw-flex tw-h-full tw-items-center tw-justify-center tw-text-center tw-text-sm tw-text-grayscale-90"
            data-cy="navbar_notifications_empty"
          >
            {activeFilter ? t('notifications.empty') : t(emptyKey)}
          </div>
        )}
        {notifications.map((notification) => (
          <NotificationRow key={notification.id} notification={notification} />
        ))}
        {hasNextPage && !isError && (
          <div
            ref={sentryRef}
            className="tw-flex tw-items-center tw-justify-center tw-p-3"
          >
            <ActivityIndicator className="tw-text-primary-100" />
          </div>
        )}
      </div>
      {footer?.({ tab, filter: activeFilter })}
    </div>
  );
}

export default NotificationsFeed;
